import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Spinner,
  Alert
} from 'reactstrap';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import {
  createLandscapePdf,
  standardAutoTableOptions,
  drawSchoolFooterBand,
  PDF_BOTTOM_MARGIN,
} from '../../utils/pdfLayout';

const baseURL = apiURL1;

function TranscriptTab() {
  const token = localStorage.getItem('token');
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);

  useEffect(() => {
    fetchStudents();
    fetchSchoolInfo();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = students.filter(student =>
        student.fullName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/superadmin/getStudentsName?limit=1000`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setStudents(response.data.students || []);
      setFilteredStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Error fetching students');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const schoolInfoResponse = await axios.get(`${baseURL}/superadmin/school-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (schoolInfoResponse.data && schoolInfoResponse.data.length > 0) {
        setSchoolInfo(schoolInfoResponse.data[0]);
      }
    } catch (error) {
      console.error('Error fetching school info:', error);
      toast.error('Error fetching school information');
    }
  };

  const fetchTranscriptData = async (studentId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/superadmin/results/all/student/${studentId}?limit=1000`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data && response.data.success !== false) {
        if (response.data.data && response.data.data.results && response.data.data.results.length > 0) {
          setTranscriptData(response.data);
          setPreviewModal(true);
        } else {
          toast.error('No academic results found for this student.');
        }
      } else {
        toast.error(response.data.message || 'No transcript data found for this student.');
      }
    } catch (error) {
      console.error('Error fetching transcript data:', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(`Error: ${error.response.data.message}`);
      } else {
        toast.error('Error fetching transcript data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateTranscriptPDF = async () => {
    if (!transcriptData) return;

    try {
      // Fetch fresh school info
      const schoolResponse = await axios.get(`${baseURL}/superadmin/school-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const schoolData = schoolResponse.data[0] || {};

      // Fetch grades information
      const gradesResponse = await axios.get(`${baseURL}/superadmin/grades`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const gradesData = gradesResponse.data.data || [];

      // Landscape so wide subject tables fit without overlapping the footer
      const pdf = createLandscapePdf(jsPDF);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentBottom = pageHeight - PDF_BOTTOM_MARGIN - 5;

      const img = new Image();
      img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";
      try {
        pdf.addImage(img, 'PNG', 12, 8, 22, 22);
      } catch (e) { /* logo optional */ }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(schoolData.schoolName || 'Ummu Dhardha Ladies Islamic College', 40, 14);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(schoolData.schoolAddress || '', 40, 20);
      pdf.text(`Tel: ${schoolData.schoolPhoneNumber || ''} | Email: ${schoolData.schoolEmail || ''}`, 40, 25);

      let yPosition = 34;

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('ACADEMIC TRANSCRIPT', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Student Information', 12, yPosition);
      yPosition += 6;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.text(`Full Name: ${transcriptData.data.fullName}`, 12, yPosition);
      yPosition += 5;
      pdf.text(`Registration Number: ${transcriptData.data.registrationNumber}`, 12, yPosition);
      yPosition += 8;

      if (transcriptData.data.results && transcriptData.data.results.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Academic Results', 12, yPosition);
        yPosition += 6;

        transcriptData.data.results.forEach((result) => {
          if (yPosition > contentBottom - 40) {
            pdf.addPage();
            yPosition = 15;
          }

          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${result.type} - ${result.year} (Class: ${result.class})`, 12, yPosition);
          yPosition += 5;
          const overallGradeDisplay = result.overallGrade || '-';
          pdf.setFont('helvetica', 'normal');
          pdf.text(
            `Total: ${result.totalMarks} | Obtained: ${result.obtainedMarks} | Percentage: ${result.overallPercentage}% | Grade: ${overallGradeDisplay}`,
            12,
            yPosition
          );
          yPosition += 4;

          // Keep columns lean to avoid mid-word wrapping
          const headRow = ['Subject', 'Code', 'Mid', 'Final', 'Total', 'Obtained', 'Grade', '%'];
          const bodyRows = (result.result || []).map((subject) => [
            subject.subjectName || '-',
            subject.subjectCode || '-',
            `${subject.midSubjectObtainedMarks ?? '-'}/${subject.midSubjectTotalMarks ?? '-'}`,
            `${subject.subjectObtainMarksFinal ?? '-'}/${subject.subjectTotalMarksFinal ?? '-'}`,
            subject.grandTotal ?? '-',
            subject.grandObtained ?? '-',
            subject.grade ?? '-',
            (subject.percentage !== undefined && subject.percentage !== null) ? `${subject.percentage}%` : '-',
          ]);

          pdf.autoTable(standardAutoTableOptions({
            head: [headRow],
            body: bodyRows,
            startY: yPosition,
            columnStyles: {
              0: { cellWidth: 55, halign: 'left' },
              1: { cellWidth: 22 },
              2: { cellWidth: 28 },
              3: { cellWidth: 28 },
              4: { cellWidth: 22 },
              5: { cellWidth: 24 },
              6: { cellWidth: 18 },
              7: { cellWidth: 22 },
            },
          }));

          yPosition = pdf.lastAutoTable.finalY + 10;
        });
      }

      if (transcriptData.data.combinedResults && transcriptData.data.combinedResults.length > 0) {
        if (yPosition > contentBottom - 40) {
          pdf.addPage();
          yPosition = 15;
        }
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Combined Results Summary', 12, yPosition);
        yPosition += 5;

        pdf.autoTable(standardAutoTableOptions({
          startY: yPosition,
          head: [['Year', 'Class', 'Mid Obtained', 'Final Obtained', 'Grand Obtained', 'Percentage', 'Grade']],
          body: transcriptData.data.combinedResults.map((cr) => [
            cr.year ?? '-',
            cr.class ?? '-',
            `${cr.midObtainedMarks ?? '-'}/${cr.midTotalMarks ?? '-'}`,
            `${cr.finalObtainedMarks ?? '-'}/${cr.finalTotalMarks ?? '-'}`,
            `${cr.grandObtainedMarks ?? '-'}/${cr.grandTotalMarks ?? '-'}`,
            `${cr.percentage ?? '-'}`,
            `${cr.grade ?? '-'}`,
          ]),
        }));
        yPosition = pdf.lastAutoTable.finalY + 8;
      }

      if (gradesData.length > 0) {
        if (yPosition > contentBottom - 50) {
          pdf.addPage();
          yPosition = 15;
        }

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Grade Information', 12, yPosition);
        yPosition += 5;

        pdf.autoTable(standardAutoTableOptions({
          head: [['Grade', 'Marks Range']],
          body: gradesData.map((grade) => [
            grade.grade,
            `${grade.startPercentage} – ${grade.endPercentage === 89 ? '89.99' : grade.endPercentage}`,
          ]),
          startY: yPosition,
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 40 },
          },
        }));

        yPosition = pdf.lastAutoTable.finalY + 10;
      }

      if (yPosition > contentBottom - 20) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text('......................................', pageWidth - 70, yPosition);
      yPosition += 5;
      pdf.text('Head of Department', pageWidth - 70, yPosition);
      yPosition += 8;
      pdf.text(`Date of Issue: ${new Date().toLocaleDateString()}`, pageWidth - 70, yPosition);

      drawSchoolFooterBand(pdf, schoolData);

      pdf.save(`transcript-${transcriptData.data.fullName.replace(/\s+/g, '-')}-${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF report');
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };

  const handleGenerateTranscript = () => {
    if (!selectedStudent) {
      toast.error('Please select a student first');
      return;
    }
    fetchTranscriptData(selectedStudent._id);
  };

  return (
    <Card className="shadow">
      <CardHeader className="bg-transparent">
        <Row className="align-items-center">
          <Col>
            <h3 className="mb-0">Student Transcript Generator</h3>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="studentSearch">Search Students</Label>
              <Input
                type="text"
                id="studentSearch"
                placeholder="Search by student name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FormGroup>

            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
              {loading ? (
                <div className="text-center p-3">
                  <Spinner color="primary" />
                </div>
              ) : (
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: '100px' }}>Select</th>
                      <th>Student Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={student._id}>
                        <td className="text-center align-middle">
                          <input
                            type="radio"
                            name="selectedStudent"
                            checked={selectedStudent?._id === student._id}
                            onChange={() => handleStudentSelect(student)}
                          />
                        </td>
                        <td>{student.fullName}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Col>

          <Col md="6">
            <div className="text-center">
              <h5>Selected Student</h5>
              {selectedStudent ? (
                <Alert color="info">
                  <strong>{selectedStudent.fullName}</strong>
                </Alert>
              ) : (
                <Alert color="warning">
                  No student selected
                </Alert>
              )}

              <Button
                color="success"
                size="sm"
                onClick={handleGenerateTranscript}
                disabled={!selectedStudent || loading}
                className="mt-3"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Generating...
                  </>
                ) : (
                  'Generate Transcript'
                )}
              </Button>
            </div>
          </Col>
        </Row>
      </CardBody>

      {/* Preview Modal */}
      <Modal isOpen={previewModal} toggle={() => setPreviewModal(false)} size="lg">
        <ModalHeader toggle={() => setPreviewModal(false)}>
          Transcript Preview - {transcriptData?.data?.fullName}
        </ModalHeader>
        <ModalBody>
          {transcriptData && (
            <div>
              {schoolInfo ? (
                <div className="text-center mb-4">
                  <h4>{schoolInfo.schoolName}</h4>
                  <p>{schoolInfo.schoolAddress}</p>
                  <p>Phone: {schoolInfo.schoolPhoneNumber} | Email: {schoolInfo.schoolEmail}</p>
                  <h5 className="mt-3">ACADEMIC TRANSCRIPT</h5>
                </div>
              ) : (
                <div className="text-center mb-4">
                  <Spinner size="sm" /> Loading school details...
                </div>
              )}

              <div className="mb-4">
                <h6>Student Information:</h6>
                <p><strong>Full Name:</strong> {transcriptData.data.fullName}</p>
                <p><strong>Registration Number:</strong> {transcriptData.data.registrationNumber}</p>
                {/* <p><strong>Overall Rank:</strong> {transcriptData.data.rank}</p> */}
              </div>

              {transcriptData.data.results && transcriptData.data.results.length > 0 && (
                <div className="mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <h6>Academic Results:</h6>
                  {transcriptData.data.results.map((result, index) => (
                    <div key={index} className="mb-3">
                      <h6>{result.type} - {result.year} (Class: {result.class})</h6>
                      <p>Total: {result.totalMarks} | Obtained: {result.obtainedMarks} | Percentage: {result.overallPercentage}% | Grade: {(() => {
                        const hasFinal = Array.isArray(result.result) && result.result.some(s => (s.finalSubjectTotalMarks !== undefined && s.finalSubjectTotalMarks !== null) || (s.finalOutsideObtained !== undefined && s.finalOutsideObtained !== null));
                        return hasFinal ? (result.overallGrade ?? '-') : '-';
                      })()}</p>

                      {(() => {
                        const set = new Set();
                        (result.result || []).forEach(subject => {
                          (subject.components || []).forEach(c => { const n = c?.component?.name; if (n) set.add(n); });
                        });
                        const componentNames = Array.from(set);
                        return (
                          <Table size="sm" bordered>
                            <thead>
                              <tr>
                                <th>Subject</th>
                                <th>Code</th>
                                <th>Subject Mid Marks</th>
                                {componentNames.map(name => (<th key={`comp-h-${name}`}>{name}</th>))}
                                <th>Subject Final Marks</th>
                                <th>Total Marks</th>
                                <th>Total Obtained</th>
                                <th>Grade</th>
                                <th>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(result.result || []).map((subject, subIndex) => (
                                <tr key={subIndex}>
                                  <td>{subject.subjectName}</td>
                                  <td>{subject.subjectCode}</td>
                                  <td>{`${(subject.midSubjectObtainedMarks ?? '-')}/${(subject.midSubjectTotalMarks ?? '-')}`}</td>
                                  {componentNames.map(name => {
                                    const comp = (subject.components || []).find(c => (c?.component?.name) === name);
                                    const obtained = comp?.marks ?? '-';
                                    const total = comp?.totalMarks ?? '-';
                                    return (
                                      <td key={`comp-${subject.subjectCode}-${name}`}>{`${obtained}/${total}`}</td>
                                    );
                                  })}
                                  <td>{`${(subject.subjectObtainMarksFinal ?? '-')}/${(subject.subjectTotalMarksFinal ?? '-')}`}</td>
                                  <td>{subject.grandTotal ?? '-'}</td>
                                  <td>{subject.grandObtained ?? '-'}</td>
                                  <td>{subject.grade}</td>
                                  <td>{subject.percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}

              {transcriptData.data.combinedResults && transcriptData.data.combinedResults.length > 0 && (
                <div className="mb-4">
                  <h6>Combined Results Summary:</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Class</th>
                        <th>Mid Obtained</th>
                        <th>Final Obtained</th>
                        <th>Grand Obtained</th>
                        <th>Percentage</th>
                        <th>Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transcriptData.data.combinedResults.map((cr, idx) => (
                        <tr key={idx}>
                          <td>{cr.year ?? '-'}</td>
                          <td>{cr.class ?? '-'}</td>
                          <td>{(cr.midObtainedMarks ?? '-')}/{(cr.midTotalMarks ?? '-')}</td>
                          <td>{(cr.finalObtainedMarks ?? '-')}/{(cr.finalTotalMarks ?? '-')}</td>
                          <td>{(cr.grandObtainedMarks ?? '-')}/{(cr.grandTotalMarks ?? '-')}</td>
                          <td>{cr.percentage ?? '-'}</td>
                          <td>{cr.grade ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Top Performing Students section removed as per user request */}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={generateTranscriptPDF}>
            Download PDF
          </Button>
          <Button color="secondary" onClick={() => setPreviewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}

export default TranscriptTab;